import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiGet, apiPatch, apiUpload, apiDelete } from '../../../api/client';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Section } from '../../../components/ui/Section';

interface StudioForm {
  studioName: string;
  studioPhone: string;
  studioWebsite: string;
  studioLogoUrl: string;
  studioSignatureUrl: string;
  brandColorPrimary: string;
  brandColorSecondary: string;
  ownerAddress: string;
  ownerCity: string;
  ownerVatNumber: string;
}

export default function StudioProfileSection() {
  const qc = useQueryClient();
  const [form, setForm] = useState<StudioForm>({
    studioName: '', studioPhone: '', studioWebsite: '', studioLogoUrl: '', studioSignatureUrl: '',
    brandColorPrimary: '', brandColorSecondary: '',
    ownerAddress: '', ownerCity: '', ownerVatNumber: '',
  });
  const [loaded, setLoaded] = useState(false);

  const { data } = useQuery<StudioForm>({
    queryKey: ['user-settings-studio'],
    queryFn: () => apiGet<StudioForm>('/user-settings/studio'),
  });

  useEffect(() => {
    if (data && !loaded) {
      setForm({
        studioName:          data.studioName          ?? '',
        studioPhone:         data.studioPhone         ?? '',
        studioWebsite:       data.studioWebsite       ?? '',
        studioLogoUrl:       data.studioLogoUrl       ?? '',
        studioSignatureUrl:  data.studioSignatureUrl  ?? '',
        brandColorPrimary:   data.brandColorPrimary   ?? '',
        brandColorSecondary: data.brandColorSecondary ?? '',
        ownerAddress:        data.ownerAddress        ?? '',
        ownerCity:           data.ownerCity           ?? '',
        ownerVatNumber:      data.ownerVatNumber      ?? '',
      });
      setLoaded(true);
    }
  }, [data, loaded]);

  const saveMutation = useMutation({
    mutationFn: () => apiPatch('/user-settings/studio', {
      studioName:          form.studioName          || null,
      studioPhone:         form.studioPhone         || null,
      studioWebsite:       form.studioWebsite       || null,
      studioLogoUrl:       form.studioLogoUrl       || null,
      brandColorPrimary:   form.brandColorPrimary   || null,
      brandColorSecondary: form.brandColorSecondary || null,
      ownerAddress:        form.ownerAddress        || null,
      ownerCity:           form.ownerCity           || null,
      ownerVatNumber:      form.ownerVatNumber      || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-settings-studio'] });
      toast.success('Profilo studio salvato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const uploadSignatureMutation = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return apiUpload<{ studioSignatureUrl: string }>('/user-settings/studio-signature', fd);
    },
    onSuccess: (data) => {
      setForm(f => ({ ...f, studioSignatureUrl: data.studioSignatureUrl }));
      toast.success('Firma caricata');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteSignatureMutation = useMutation({
    mutationFn: () => apiDelete('/user-settings/studio-signature'),
    onSuccess: () => {
      setForm(f => ({ ...f, studioSignatureUrl: '' }));
      toast.success('Firma rimossa');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const set = (key: keyof StudioForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [key]: e.target.value }));

  return (
    <Section title="Dati studio">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <Input label="Nome studio" value={form.studioName} onChange={set('studioName')} placeholder="Studio Fotografico Rossi" />
        <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <Input label="Email contatto" value="" disabled placeholder="Gestita dal profilo utente" hint="Modifica l'email dal profilo utente" />
          <Input label="Telefono" value={form.studioPhone} onChange={set('studioPhone')} placeholder="+39 02 1234567" />
        </div>
        <Input label="Indirizzo" value={form.ownerAddress} onChange={set('ownerAddress')} placeholder="Via Roma 1" />
        <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <Input label="Città" value={form.ownerCity} onChange={set('ownerCity')} placeholder="Milano" />
          <Input label="P.IVA" value={form.ownerVatNumber} onChange={set('ownerVatNumber')} placeholder="01234567890" />
        </div>
        <Input label="Sito web" value={form.studioWebsite} onChange={set('studioWebsite')} placeholder="https://www.studiofotorossi.it" />

        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '1rem', marginTop: '0.5rem' }}>
          <p style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>
            Logo e colori brand
          </p>
          <Input label="URL logo" value={form.studioLogoUrl} onChange={set('studioLogoUrl')} placeholder="https://... o /uploads/logo.png" />
          {form.studioLogoUrl && (
            <div style={{ marginTop: '0.5rem' }}>
              <img src={form.studioLogoUrl} alt="Logo studio" style={{ maxHeight: 60, borderRadius: 'var(--radius-interactive)' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            </div>
          )}
          <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '0.75rem' }}>
            <div>
              <label style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.25rem' }}>Colore primario</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input type="color" value={form.brandColorPrimary || '#3b82f6'} onChange={e => setForm(f => ({ ...f, brandColorPrimary: e.target.value }))} style={{ width: 40, height: 32, border: 'none', cursor: 'pointer', borderRadius: 4 }} />
                <Input value={form.brandColorPrimary} onChange={set('brandColorPrimary')} placeholder="#3b82f6" style={{ flex: 1 }} />
              </div>
            </div>
            <div>
              <label style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.25rem' }}>Colore secondario</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input type="color" value={form.brandColorSecondary || '#64748b'} onChange={e => setForm(f => ({ ...f, brandColorSecondary: e.target.value }))} style={{ width: 40, height: 32, border: 'none', cursor: 'pointer', borderRadius: 4 }} />
                <Input value={form.brandColorSecondary} onChange={set('brandColorSecondary')} placeholder="#64748b" style={{ flex: 1 }} />
              </div>
            </div>
          </div>
        </div>

        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '1rem', marginTop: '0.5rem' }}>
          <p style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>
            Firma digitale contratto
          </p>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', marginBottom: '0.75rem' }}>
            Carica un file PNG con la tua firma. Verrà inserita nei contratti al posto della linea di firma testuale.
          </p>
          {form.studioSignatureUrl ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <img src={form.studioSignatureUrl} alt="Firma digitale" style={{ maxHeight: 80, maxWidth: 200, border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)', padding: '0.5rem', background: '#fff' }} />
              <Button variant="ghost" onClick={() => deleteSignatureMutation.mutate()} loading={deleteSignatureMutation.isPending} style={{ color: 'var(--color-danger, #e53e3e)' }}>
                Rimuovi firma
              </Button>
            </div>
          ) : (
            <div>
              <input
                type="file"
                accept="image/png"
                style={{ display: 'none' }}
                id="signature-upload"
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) uploadSignatureMutation.mutate(file);
                  e.target.value = '';
                }}
              />
              <label
                htmlFor="signature-upload"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.5rem 1rem', borderRadius: 'var(--radius-card)',
                  border: '1px solid rgba(255,255,255,0.10)', cursor: 'pointer',
                  fontSize: 'var(--text-sm)', fontWeight: 500,
                  background: 'var(--glass-bg-card)', color: 'var(--color-text)',
                }}
              >
                {uploadSignatureMutation.isPending ? 'Caricamento...' : 'Carica firma (PNG)'}
              </label>
            </div>
          )}
        </div>

        <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending}>
          Salva profilo studio
        </Button>
      </div>
    </Section>
  );
}
